import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import Layout from "@/components/layout/Layout";
import AppToaster from "@/components/layout/AppToaster";
import { AuthProvider } from "@/hooks/useAuth";
import RequireAuth from "@/components/auth/RequireAuth";
import { appRoutes } from "@/routesConfig";
import { initializeAnalytics } from "@/utils/analytics";
import { usePageTracking } from "@/hooks/usePageTracking";
import RouteMeta from "@/components/layout/RouteMeta";
import "@/i18n/config"; // Initialize i18n

// Create a query cache with size limits to prevent unbounded memory growth
const queryCache = new QueryCache({
  // Optional: Add logging for cache operations in development
  onError: (error, query) => {
    console.error("Query error:", error, query.queryKey);
  },
});

const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      // Never retry on client errors (4xx) — they will fail again immediately
      // and cause a request storm against the rate limiter.
      retry: (failureCount, error: unknown) => {
        const status =
          (error as { response?: { status?: number } })?.response?.status;
        if (status !== undefined && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
      // Exponential back-off (1 s, 2 s, …) so retries don't immediately
      // hammer the server after a transient 5xx or network error.
      retryDelay: (attemptIndex) =>
        Math.min(1000 * 2 ** attemptIndex, 30000),
      // Cache telemetry data for 10 minutes to prevent excessive API calls
      gcTime: 1000 * 60 * 10, // 10 minutes (garbage collection time)
    },
  },
});

// Component to track page views - must be inside Router
function PageTracker() {
  usePageTracking();
  return null;
}

function App() {
  // Initialize Google Analytics on app mount
  useEffect(() => {
    initializeAnalytics();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <RouteMeta />
        <PageTracker />
        <AuthProvider>
          <Routes>
            {appRoutes.map((route) => {
              const element = route.public ? (
                <route.component />
              ) : (
                <RequireAuth>
                  <route.component />
                </RequireAuth>
              );

              // Standalone routes (outside Layout)
              if (route.standalone) {
                return (
                  <Route key={route.path} path={route.path} element={element} />
                );
              }

              // Routes inside Layout
              return null; // These will be handled below
            })}

            <Route path="/" element={<Layout />}>
              {appRoutes
                .filter((route) => !route.standalone)
                .map((route) => {
                  const element = route.public ? (
                    <route.component />
                  ) : (
                    <RequireAuth>
                      <route.component />
                    </RequireAuth>
                  );

                  // Index route
                  if (route.path === "/") {
                    return <Route key={route.path} index element={element} />;
                  }

                  return (
                    <Route
                      key={route.path}
                      path={route.path.slice(1)}
                      element={element}
                    />
                  );
                })}
            </Route>
          </Routes>
        </AuthProvider>
      </Router>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      <AppToaster />
    </QueryClientProvider>
  );
}

export default App;
